import { useState, useCallback } from 'react';
import { GiftRecord, AddGiftRecordRequest, UpdateGiftRecordRequest, RSVPResponse } from '../../../types/invitation';
import { giftService } from '../../../lib/supabase';
import { createLogger } from '../../../lib/logger';
import { GiftFormData, INITIAL_FORM_DATA } from '../types';

const log = createLogger('gift');

interface UseGiftFormOptions {
  invitationId: string;
  onSaved: () => Promise<void>;
  onNavigateToRecords: () => void;
}

export function useGiftForm({ invitationId, onSaved, onNavigateToRecords }: UseGiftFormOptions) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<GiftRecord | null>(null);
  const [formData, setFormData] = useState<GiftFormData>(INITIAL_FORM_DATA);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setFormErrors({});
    setEditingRecord(null);
  }, []);

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.guestName.trim()) {
      errors.guestName = '請輸入賓客姓名';
    }

    if (formData.amount <= 0) {
      errors.amount = '金額必須大於 0';
    }

    if (formData.guestEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guestEmail)) {
      errors.guestEmail = '請輸入有效的電子郵件地址';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);

      if (editingRecord) {
        const updateRequest: UpdateGiftRecordRequest = {
          recordId: editingRecord.id,
          guestName: formData.guestName,
          guestEmail: formData.guestEmail || undefined,
          amount: formData.amount,
          currency: formData.currency,
          giftType: formData.giftType,
          giftDescription: formData.giftDescription || undefined,
          message: formData.message || undefined,
        };
        await giftService.updateGiftRecord(updateRequest);
      } else {
        const addRequest: AddGiftRecordRequest = {
          invitationId,
          rsvpId: formData.rsvpId,
          guestName: formData.guestName,
          guestEmail: formData.guestEmail || undefined,
          amount: formData.amount,
          currency: formData.currency,
          giftType: formData.giftType,
          giftDescription: formData.giftDescription || undefined,
          message: formData.message || undefined,
        };
        await giftService.addGiftRecord(addRequest);
      }

      await onSaved();
      resetForm();
      setShowAddForm(false);
      onNavigateToRecords();
    } catch (error) {
      log.error('儲存紅包記錄失敗:', error);
      alert(`儲存失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setSaving(false);
    }
  }, [validateForm, editingRecord, formData, invitationId, onSaved, resetForm, onNavigateToRecords]);

  const handleEdit = useCallback((record: GiftRecord) => {
    setFormData({
      guestName: record.guestName,
      guestEmail: record.guestEmail || '',
      amount: record.amount,
      currency: record.currency,
      giftType: record.giftType,
      giftDescription: record.giftDescription || '',
      message: record.message || '',
      rsvpId: record.rsvpId,
    });
    setEditingRecord(record);
    setShowAddForm(true);
  }, []);

  const handleDelete = useCallback(async (recordId: string, onRefresh: () => Promise<void>) => {
    if (!confirm('確定要刪除這筆紅包記錄嗎？')) return;

    try {
      setDeleting(recordId);
      await giftService.deleteGiftRecord(recordId);
      await onRefresh();
    } catch (error) {
      log.error('刪除紅包記錄失敗:', error);
      alert(`刪除失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setDeleting(null);
    }
  }, []);

  const fillFromRSVP = useCallback((rsvp: RSVPResponse) => {
    setFormData(prev => ({
      ...prev,
      guestName: rsvp.guestName,
      guestEmail: rsvp.guestEmail,
      rsvpId: rsvp.id,
    }));
  }, []);

  return {
    showAddForm,
    setShowAddForm,
    editingRecord,
    formData,
    setFormData,
    formErrors,
    saving,
    deleting,
    resetForm,
    handleSubmit,
    handleEdit,
    handleDelete,
    fillFromRSVP,
  };
}
